import React, { FC, ReactNode } from 'react';
import { TypographyWithElipsis } from '@components/TypographyElipsis';
import { Box, MenuItem, Stack, Typography } from '@mui/material';
import { categorySummary } from '@store/zustand/model-filters';
import { ArrowBigRightIcon } from '@utils/iconsDefs';

type Props = {
  icon: ReactNode;
  label: string;
  values: string[];
  labels: Record<string, string>;
  onClick: () => void;
};

/**
 * One category row in the Filters root popover.
 *
 * The secondary line is the AC3.2 summary ladder rather than a value list,
 * and the dot is AC4.4's marker for a category that carries a selection. The
 * row owns no selection state: it renders what it is given and reports the
 * click, so the panel above it decides what opens. The leading slot keeps its
 * 20px width when no icon is supplied, so the labels of a mixed list stay
 * aligned. Row padding, gap and hover come from the `MuiMenuItem` theme
 * override, which already matches the Figma row.
 */
const FilterCategoryRow: FC<Props> = ({
  icon,
  label,
  values,
  labels,
  onClick,
}) => {
  const hasSelection = values.length > 0;

  return (
    <MenuItem onClick={onClick}>
      <Box display="flex" width={20} height={20} flexShrink={0}>
        {icon}
      </Box>

      <Stack flex={1} minWidth={0}>
        <Typography variant="h5" color="grey.1000" noWrap>
          {label}
        </Typography>

        <TypographyWithElipsis
          variant="body2"
          color="grey.600"
          tooltipPlacement="bottom"
        >
          {categorySummary(values, labels)}
        </TypographyWithElipsis>
      </Stack>

      {hasSelection && (
        <Box
          aria-hidden
          data-testid="filter-category-selected-dot"
          flexShrink={0}
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'primary.main',
          }}
        />
      )}

      <Box display="flex" width={20} height={20} flexShrink={0}>
        <ArrowBigRightIcon width={20} height={20} />
      </Box>
    </MenuItem>
  );
};

export default FilterCategoryRow;
